import type { NavSectionProps, NavItemDataProps } from 'src/components/nav-section';

import { useMemo, useState } from 'react';
import { isActiveLink, isExternalLink } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Drawer from '@mui/material/Drawer';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';

import { useRouter, usePathname } from 'src/routes/hooks';

import { Iconify } from 'src/components/iconify';

const MAX_VISIBLE_TABS = 4;
const MORE_VALUE = '__more__';

type NavBottomProps = {
  data: NavSectionProps['data'];
  checkPermissions?: NavSectionProps['checkPermissions'];
};

export function NavBottom({ data, checkPermissions }: NavBottomProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [moreOpen, setMoreOpen] = useState(false);

  const { visible, hidden } = useMemo(
    () => splitNavData(data, checkPermissions),
    [data, checkPermissions]
  );

  if (visible.length === 0 && hidden.length === 0) return null;

  const overflow = hidden.length > 0;
  const items = [...visible, ...hidden];

  const activeItem = items.find((item) => isActive(pathname, item));
  const activeInHidden = activeItem && hidden.includes(activeItem);
  const value = activeInHidden ? MORE_VALUE : (activeItem?.path ?? false);

  const handleChange = (_: unknown, next: string) => {
    if (next === MORE_VALUE) {
      setMoreOpen(true);
      return;
    }
    if (next === pathname) return;
    if (isExternalLink(next)) {
      window.open(next, '_blank', 'noopener,noreferrer');
      return;
    }
    router.push(next);
  };

  const handlePickHidden = (item: NavItemDataProps) => {
    setMoreOpen(false);
    if (item.path === pathname) return;
    if (isExternalLink(item.path)) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
      return;
    }
    router.push(item.path);
  };

  return (
    <>
      <Paper
        elevation={3}
        sx={(theme) => ({
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          borderTop: '1px solid',
          borderColor: 'var(--layout-nav-border-color, divider)',
          bgcolor: 'var(--layout-nav-bg, background.paper)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          display: { xs: 'block', sm: 'none' },
        })}
      >
        <BottomNavigation
          showLabels
          value={value}
          onChange={handleChange}
          sx={{
            height: 64,
            bgcolor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              color: 'var(--layout-nav-text-secondary-color, text.secondary)',
              minWidth: 0,
              padding: '6px 8px 8px',
            },
            '& .MuiBottomNavigationAction-root.Mui-selected': {
              color: 'var(--layout-nav-text-primary-color, text.primary)',
            },
          }}
        >
          {visible.map((item) => (
            <BottomNavigationAction
              key={item.path}
              value={item.path}
              label={item.title}
              disabled={item.disabled}
              icon={renderIcon(item.icon)}
            />
          ))}
          {overflow && (
            <BottomNavigationAction
              key={MORE_VALUE}
              value={MORE_VALUE}
              label="More"
              icon={<Iconify icon="solar:menu-dots-bold" width={24} />}
            />
          )}
        </BottomNavigation>
      </Paper>

      {overflow && (
        <Drawer
          anchor="bottom"
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              },
            },
          }}
        >
          <Box
            sx={{ width: 36, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', my: 1.5 }}
          />
          <List sx={{ px: 1, pb: 1 }}>
            {hidden.map((item) => (
              <ListItemButton
                key={item.path}
                selected={isActive(pathname, item)}
                disabled={item.disabled}
                onClick={() => handlePickHidden(item)}
                sx={{ borderRadius: 1 }}
              >
                {item.icon && (
                  <ListItemIcon sx={{ minWidth: 36 }}>{renderIcon(item.icon)}</ListItemIcon>
                )}
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      )}
    </>
  );
}

export function splitNavData(
  data: NavSectionProps['data'],
  checkPermissions?: NavSectionProps['checkPermissions']
): { visible: NavItemDataProps[]; hidden: NavItemDataProps[] } {
  const allowed = data.flatMap((group) =>
    group.items.filter((item) => {
      if (!item.path) return false;
      if (checkPermissions && !checkPermissions(item.allowedRoles)) return false;
      return true;
    })
  );

  const primaries = allowed.filter((item) => item.primary === true).slice(0, MAX_VISIBLE_TABS);

  if (primaries.length > 0) {
    const primarySet = new Set(primaries);
    return { visible: primaries, hidden: allowed.filter((item) => !primarySet.has(item)) };
  }

  return {
    visible: allowed.slice(0, MAX_VISIBLE_TABS),
    hidden: allowed.slice(MAX_VISIBLE_TABS),
  };
}

function isActive(pathname: string, item: NavItemDataProps): boolean {
  return isActiveLink(pathname, item.path, !!item.deepMatch);
}

function renderIcon(icon: NavItemDataProps['icon']): React.ReactNode {
  if (!icon) return undefined;
  if (typeof icon === 'string') return <Iconify icon={icon} width={24} />;
  return icon;
}
